import { SubscriptionV2Variable } from "@/pages/subscriptions/services/types";
import type { FilterFormData } from "@/pages/subscriptions/services/types";
import { SubscriptionType, VariableType } from "@/pages/subscriptions/constants";

/**
 * Generate a filter summary string from filters
 * Example: "Warehouse: WH_PROD, WH_DEV AND Instance: rakuten-prod"
 */
function generateFilterSummary(filters: FilterFormData[]): string {
  if (filters.length === 0) return "";

  return filters.map(filter => `${filter.dimension}: ${filter.value}`).join(" AND ");
}

/**
 * Create entity variable from source and filters (from previous step)
 */
function createEntityVariable(
  source: string,
  filters: FilterFormData[],
  categoryDisplayName?: string
): SubscriptionV2Variable {
  const filterCount = filters.length;
  const displayValue = categoryDisplayName || capitalizeFirst(source);

  const tooltip =
    filterCount > 0
      ? `Filters applied:\n${generateFilterSummary(filters)}\n\nGo back to Subscription Type to edit.`
      : `Go back to Subscription Type to edit the ${source} selection.`;

  return {
    id: "entities",
    variableId: "entities", // Keep original ID since useFieldArray will overwrite 'id'
    type: VariableType.ENTITY,
    value: `all_${source}s`,
    displayValue,
    config: {
      isEditable: false,
    },
    fromPreviousStep: true,
    tooltip,
    filterCount: filterCount > 0 ? filterCount : undefined,
  };
}

/**
 * Create metric variable from metric (from previous step)
 * Uses display_name from backend metrics API
 */
function createMetricVariable(metric: string, displayName?: string): SubscriptionV2Variable {
  const resolvedDisplayName = displayName || capitalizeFirst(metric);

  return {
    id: "metric",
    variableId: "metric",
    type: VariableType.METRIC,
    value: metric,
    displayValue: resolvedDisplayName,
    config: {
      isEditable: false,
    },
    fromPreviousStep: true,
    tooltip: `Selected metric: ${resolvedDisplayName}\n\nGo back to Subscription Type to edit.`,
  };
}

/**
 * Create aggregation variable with default value
 * Uses backend-provided default or fallback to "mean"
 */
function createAggregationVariable(backendDefault?: string): SubscriptionV2Variable {
  const value = backendDefault || "mean";
  const displayValue = value.charAt(0).toUpperCase() + value.slice(1);

  return {
    id: "aggregation",
    variableId: "aggregation",
    type: VariableType.AGGREGATION,
    value,
    displayValue,
    config: {
      options: [
        { label: "Mean", value: "mean" },
        { label: "Sum", value: "sum" },
        { label: "Maximum", value: "max" },
        { label: "Minimum", value: "min" },
      ],
      isEditable: true,
    },
  };
}

/**
 * Create change type variable with default value (alert only)
 * Uses backend-provided default or fallback to "relative_value"
 */
function createChangeTypeVariable(backendDefault?: string): SubscriptionV2Variable {
  const value = backendDefault || "relative_value";
  const displayMap: Record<string, string> = {
    relative_value: "Relative value",
    absolute_value: "Absolute value",
  };
  const displayValue = displayMap[value] || value;

  return {
    id: "change_type",
    variableId: "change_type",
    type: VariableType.CHANGE_TYPE,
    value,
    displayValue,
    config: {
      options: [
        { label: "Relative value", value: "relative_value" },
        { label: "Absolute value", value: "absolute_value" },
      ],
      isEditable: true,
    },
  };
}

/**
 * Create comparison variable with default value (alert only)
 * Uses backend-provided default or fallback to "exceeds"
 */
function createComparisonVariable(backendDefault?: string): SubscriptionV2Variable {
  const value = backendDefault || "exceeds";
  const displayMap: Record<string, string> = {
    changes_by: "Changes by",
    exceeds: "Exceeds",
    below: "Below",
  };
  const displayValue = displayMap[value] || value;

  return {
    id: "comparison",
    variableId: "comparison",
    type: VariableType.COMPARISON,
    value,
    displayValue,
    config: {
      options: [
        { label: "Changes by", value: "changes_by" },
        { label: "Exceeds", value: "exceeds" },
        { label: "Below", value: "below" },
      ],
      isEditable: true,
    },
  };
}

/**
 * Create threshold variable with default value (alert only)
 * Uses backend-provided default or fallback to "100"
 */
function createThresholdVariable(backendDefault?: string): SubscriptionV2Variable {
  const value = backendDefault || "100";
  const displayValue = value;

  return {
    id: "threshold",
    variableId: "threshold",
    type: VariableType.THRESHOLD,
    value,
    displayValue,
    config: {
      isEditable: true,
    },
  };
}

/**
 * Create time period variable with default value (alert only)
 * Uses backend-provided default or fallback to "previous_day"
 */
function createTimePeriodVariable(backendDefault?: string): SubscriptionV2Variable {
  const value = backendDefault || "previous_day";
  const displayMap: Record<string, string> = {
    previous_day: "Previous day",
    previous_week: "Previous week",
    previous_month: "Previous month",
    previous_year: "Previous year",
  };
  const displayValue = displayMap[value] || value;

  return {
    id: "time_period",
    variableId: "time_period",
    type: VariableType.DATE_RANGE,
    value,
    displayValue,
    config: {
      options: [
        { label: "Previous day", value: "previous_day" },
        { label: "Previous week", value: "previous_week" },
        { label: "Previous month", value: "previous_month" },
        { label: "Previous year", value: "previous_year" },
      ],
      isEditable: true,
    },
  };
}

/**
 * Create schedule variable with default value
 * Value format: JSON string with { frequency, time, daysOfWeek?, dayOfMonth? }
 */
function createScheduleVariable(): SubscriptionV2Variable {
  const defaultSchedule = {
    frequency: "daily",
    time: "2:00 PM",
  };

  return {
    id: "schedule",
    variableId: "schedule",
    type: VariableType.SCHEDULE,
    value: JSON.stringify(defaultSchedule),
    displayValue: "Daily at 2:00 PM",
    config: {
      isEditable: true,
    },
  };
}

/**
 * Build report prompt template
 */
function buildReportPrompt(): string {
  return "Send me a report for {{entities}} of the {{aggregation}} of {{metric}} {{schedule}}";
}

/**
 * Build alert prompt template
 * Always includes change_type so users can switch between absolute/relative
 * Always includes time_period (date_range) for both types:
 * - For absolute_value: specifies the analysis period
 * - For relative_value: specifies the comparison period
 * Uses a unified prompt structure that works for both cases
 */
function buildAlertPrompt(): string {
  // Unified prompt that works for both absolute and relative values
  // The time_period applies as the analysis window (absolute) or comparison period (relative)
  return "Send me an alert for {{entities}} when the {{change_type}} of the {{aggregation}} of {{metric}} over the {{time_period}} {{comparison}} {{threshold}}. Check the entities for these changes {{schedule}}";
}

/**
 * Main function to generate prompt and variables from form data
 */
export function generatePromptFromFormData(
  formData: {
    rule_type: SubscriptionType;
    source: string;
    metric: string;
    filters: FilterFormData[];
  },
  metricDefaults?: {
    default_aggregation?: string;
    default_comparison_type?: string;
    default_threshold_type?: string;
    default_threshold?: string;
    default_date_range?: string;
    metric_display_name?: string;
    category_display_name?: string;
  }
): { prompt: string; variables: SubscriptionV2Variable[] } {
  const isAlert = formData.rule_type === SubscriptionType.ALERT;

  // Build variable list
  const variables: SubscriptionV2Variable[] = [
    createEntityVariable(formData.source, formData.filters, metricDefaults?.category_display_name),
  ];

  // For alerts, always include the change_type variable so users can switch between absolute/relative
  if (isAlert) {
    variables.push(createChangeTypeVariable(metricDefaults?.default_comparison_type));
  }

  variables.push(
    createAggregationVariable(metricDefaults?.default_aggregation),
    createMetricVariable(formData.metric, metricDefaults?.metric_display_name)
  );

  if (isAlert) {
    variables.push(
      createComparisonVariable(metricDefaults?.default_threshold_type),
      createThresholdVariable(metricDefaults?.default_threshold),
      // Always add time_period for alerts (both absolute and relative)
      createTimePeriodVariable(metricDefaults?.default_date_range)
    );
  }

  variables.push(createScheduleVariable());

  // Build prompt template
  const prompt = isAlert ? buildAlertPrompt() : buildReportPrompt();

  return { prompt, variables };
}

// Helper functions

function capitalizeFirst(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1);
}
